// --------------------------------------------------------------------------
// File to define general customization of chakra-ui components.
// --------------------------------------------------------------------------

import { extendTheme } from "@chakra-ui/react";
import "./fonts.css";

// General size of components
const defaultSizeNormal = "sm";

// Color palette
const colors = {
	box: "#242424ef",
	drawer: "#242424",
	highlightLight: "#EAE9E0", // From map style adapted
	highlightDark: "#7A8949", // From map style adapted
	selected: "#EAE9E0", // or Water from map style "hsl(199, 44%, 59%)"
	warning: "red.500",
	results: "whiteAlpha.800",
	white: "white",
	black: "black",
};

const theme = extendTheme({
	// General
	styles: {
    global: {
      ':root': {
        '--chakra-shadows-outline': '0 0 0 2px rgba(255, 255, 255, 0.6)',
      },
    },
  },

	// Colors
	colors: {
    main: colors,
  },

	// Border radius
	radii: {
		box: "md",
		card: "md",
	},

	// Font size
	fontSize: {
		iconLeft: "md",
		iconSingle: "0.9rem",
		info: "xs",
		body: "sm",
		cardName: "md",
		topic: "lg",
		alertName: "xl",
		heading: "2xl",
	},

	// Components
  components: {

		Heading: {
			baseStyle: {
				fontFamily: "heading",
				color: colors.white,
			},
			defaultProps: {
			},
		},
		
		Text: {
			baseStyle: {
				fontFamily: "body",
				color: colors.white,
			},
			variants: {
				results: {
					fontFamily: "body",
					color: colors.results,
				},
			},

			defaultProps: {
			},
		},

		FormLabel: {
			baseStyle: {
				fontFamily: "label",
				color: colors.white,
			},
			defaultProps: {
			},
		},
		
		Input: {
			baseStyle:{
				fontFamily: "body",
				color: colors.white,
			},
			defaultProps:{
				size: defaultSizeNormal,
				focusBorderColor: colors.white,
			},
		},

		Textarea: {
			baseStyle:{
				fontFamily: "body",
				color: colors.white,
			},
			defaultProps:{
				size: defaultSizeNormal,
				focusBorderColor: colors.white,
			},
		},

		NumberInput: {
			baseStyle:{
				fontFamily: "body",
				color: colors.white,
			},
			defaultProps:{
				size: defaultSizeNormal,
				focusBorderColor: colors.white,
			},
		},

		Slider: {
			baseStyle: {
				container: {
				},
				thumb: {
					bg: colors.selected,
				},
				track: {
					bg: colors.drawer,
				},
				filledTrack: {
					bg: colors.selected,
				},
				mark: {
					color: colors.results,
				},
			},
			defaultProps: {
				size: defaultSizeNormal,
			}
		},
		
		Radio: {
			baseStyle:{
				control: {
					_checked: {
						bg: colors.selected,
						borderColor: colors.selected,
						color: "white",
						_hover: {
							bg: colors.selected,
							borderColor: colors.selected,
						},
						_focus: {
							ring: 2,
							ringColor: colors.selected,
						},
					},
					_focus: {
						ring: 2,
						ringColor: colors.selected,
					},
				},
			},
			defaultProps:{
				size: defaultSizeNormal,
			},
		},

		Checkbox: {
			baseStyle: {
				control: {
					_checked: {
						bg: colors.selected,
						borderColor: colors.selected,
						color: "white",
						_hover: {
							bg: colors.selected,
							borderColor: colors.selected,
						},
						_focus: {
							ring: 2,
							ringColor: colors.selected,
						},
					},
					_focus: {
						ring: 2,
						ringColor: colors.selected,
					},
				},
			},
			defaultProps:{
				size: defaultSizeNormal,
			},
		},

		Switch: {
			baseStyle: {
				container: {
  			},
  			thumb: {
					_checked: {
						bg: colors.highlightDark,
					},
  			},
  			track: {
  			  _checked: {
  			    bg: colors.selected,
  			  },
  			},
			},
			defaultProps:{
				size: defaultSizeNormal,
			},
		},

    Button: {
			baseStyle: {
				fontFamily: "monoThin",
				lineHeight: "normal",
			},
			variants: {
				standard: {
					color: colors.highlightDark,
					bg: colors.highlightLight,
					fontFamily: "monoThin",
					lineHeight: "normal",
				}
			},
			defaultProps: {
				size: defaultSizeNormal, 
			},
		},

		IconButton: {
			variants: {
				plain: {
					color: "white",
					bg: "transparent",
				},
			},
			defaultProps: {
				size: defaultSizeNormal, 
			},
		},

  },

	// Define font and font-weight only here.
	fonts: {
		title: `"BebasNeue-Regular", sans-serif`,
		headingBold: `"HostGrotesk-ExtraBold", sans-serif`,
		heading: `"HostGrotesk-Bold", sans-serif`,
		label: `"HostGrotesk-Medium", sans-serif`,
		body: `"HostGrotesk-Light", sans-serif`,
		monoThin: `"RobotoMono-Light", sans-serif`,
		mono: `"RobotoMono-Regular", sans-serif`,
	},
});

export default theme;